import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PerformanceGoal {
  id: string;
  employee_id: string;
  goal_type: string;
  title: string;
  description?: string;
  target_value: number;
  current_value: number;
  unit?: string;
  start_date: string;
  end_date: string;
  status: string;
  priority: string;
  incentive_amount?: number;
  incentive_currency: string;
  progress_percentage: number;
  completion_date?: string;
  notes?: string;
  metadata?: Record<string, any>;
  created_at: string;
  employees?: {
    id: string;
    name: string;
  };
}

export const usePerformanceGoals = (employeeId?: string, status?: string) => {
  return useQuery({
    queryKey: ["performance-goals", employeeId, status],
    queryFn: async () => {
      let query = supabase
        .from("performance_goals")
        .select(`
          *,
          employees!performance_goals_employee_id_fkey(id, name)
        `)
        .order("created_at", { ascending: false });

      if (employeeId) {
        query = query.eq("employee_id", employeeId);
      }

      if (status) {
        query = query.eq("status", status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PerformanceGoal[];
    },
  });
};

export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (goal: any) => {
      const { data, error } = await supabase
        .from("performance_goals")
        .insert([goal])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance-goals"] });
      toast.success("تم إنشاء الهدف بنجاح");
    },
    onError: (error: any) => {
      toast.error("فشل إنشاء الهدف: " + error.message);
    },
  });
};

export const useUpdateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PerformanceGoal> }) => {
      const { data: result, error } = await supabase
        .from("performance_goals")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance-goals"] });
      toast.success("تم تحديث الهدف بنجاح");
    },
    onError: (error: any) => {
      toast.error("فشل تحديث الهدف: " + error.message);
    },
  });
};